import { configureStore } from '@reduxjs/toolkit';
import templateJsonReducer, { findElementById, findElementByTemplateId } from './TemplateJsonSlice';
import { editorJsonVersionStore } from './EditorJsonVersionStore';
import { deleteAllEventScript } from './EventItemSlice';
import eventItemStore from './EventItemStore';

// 履歴管理クラスをインポート
import { templateHistoryManager } from './TemplateHistoryManager';

/**
 * 変更履歴を記録するミドルウェア
 * アクションが適用される「前」と「後」の実際のデータ状態を比較し、
 * 実際に中身に変更が発生したアクションの直前状態のみを履歴に記録します。
 * これにより、無駄な履歴蓄積や、やり直し・元に戻す処理での巻き戻し飛びを徹底的に防ぎます。
 */
const historyMiddleware = store => next => action => {
  const skipActions = [
    'templateJson/loadTemplateJson',
    'templateJson/reload',
    'templateJson/updateExpand'
  ];

  // AIバッチ処理の開始（描画停止）
  if (action.type === 'templateJson/setReloadOff') {
    templateHistoryManager.startBatch(store);
    return next(action);
  }

  // AIバッチ処理の終了（描画再開）
  if (action.type === 'templateJson/setReloadOn') {
    templateHistoryManager.endBatch(store);
    return next(action);
  }

  const shouldRecord = action.type &&
    action.type.startsWith('templateJson/') &&
    !skipActions.includes(action.type);

  let prevStateCopy = null;

  // 1. 変更前の状態をキャプチャして一時保存
  if (shouldRecord && !templateHistoryManager.isRestoring && !templateHistoryManager.isBatching) {
    prevStateCopy = templateHistoryManager.createStateCopy(store);
  }

  // 2. アクションを実行し、レデューサーに状態を適用
  const result = next(action);

  // 3. アクション実行前後のデータをシリアライズして比較
  if (prevStateCopy) {
    const postStateString = templateHistoryManager._getStateString(store);
    const prevStateString = templateHistoryManager._serializeState(prevStateCopy);

    // データに実際の変化（DOM、イベント、属性の追加・削除・変更等）があった場合のみ、
    // キャプチャしておいた変更前の状態を過去履歴（past）として確定追加します。
    if (prevStateString !== postStateString) {
      templateHistoryManager.pushToPast(prevStateCopy, action.type);
    }
  }

  return result;
};

/**
 * ゴミ箱移動時にイベント紐付けを自動解除するミドルウェア
 * UI操作、AIコマンド、その他いかなる経路での削除でも確実にクリーンアップを実行します。
 */
const eventCleanupMiddleware = store => next => action => {

  // 1. ゴミ箱への移動 または 完全削除アクションを検知
  if (action.type === 'templateJson/moveToTrashbox' || action.type === 'templateJson/deleteElementPermanently') {
    const state = store.getState().templateJson;
    const { targetId } = action.payload;

    // 削除される要素を特定
    const targetElement = findElementById(state, targetId);

    // 要素が存在すれば、再帰的にイベント削除を実行
    if (targetElement) {
      recursiveDispatchDeleteEvent(targetElement);
    }
  }
  // 2. template-id指定での削除アクションを検知 (AIがページ削除時によく使用)
  else if (action.type === 'templateJson/deleteTemplateByAttributeId') {
    const state = store.getState().templateJson;
    const { templateId } = action.payload;

    // template-id属性から要素を特定
    const targetElement = findElementByTemplateId(state, templateId);

    if (targetElement) {
      recursiveDispatchDeleteEvent(targetElement);
    }
  }

  // 本来のアクション（ゴミ箱移動）を続行
  return next(action);
};

/**
 * 再帰的に要素とその子要素のイベント紐付けを削除するヘルパー関数
 */
function recursiveDispatchDeleteEvent(element) {
  // この要素IDに紐づくイベントスクリプトを全て削除
  eventItemStore.dispatch(deleteAllEventScript({ templateId: element.id }));

  // 子要素があれば再帰的に処理
  if (element.children && element.children.length > 0) {
    element.children.forEach(child => recursiveDispatchDeleteEvent(child));
  }
}

const templateJsonStore = configureStore({
  reducer: {
    templateJson: templateJsonReducer
  },
  // historyMiddleware を eventCleanupMiddleware よりも先に登録することで、
  // イベントが削除される前の「完全な状態」を履歴として録画できるようにします。
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({ serializableCheck: false }).concat(historyMiddleware, eventCleanupMiddleware),
});

export default templateJsonStore;